import {useMemo} from 'react';
import type {Booking} from '../../api/types';
import {useRouter} from '../../app/router';
import {href, QR_PARAMS, ROUTES} from '../../app/routes';
import {dateLabel, findBookingByToken, splitBookingTime, useManagedBooking} from '../../booking';
import {useI18n, type Language, type MessageKey, type MessageParams} from '../../i18n';
import {
  ActionLink,
  Card,
  Screen,
  ScreenBody,
  ScreenFooter,
  SplitRow,
  Stack,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # BookDone · confirmation
 *
 * Route `/book/done/:token?m=<tenant_id>`. Reached with the `manage_token` the
 * create call served once — carried in the URL by BookWeb and persisted the
 * instant it arrived (`src/booking/store.ts`).
 *
 * It renders from the stored snapshot immediately (a guest who just booked sees
 * it with no spinner) while a `GET` confirms it in the background; a refresh, or
 * the link opened on another device, falls back to that read. A link that no
 * longer resolves is an honest dead end (`book.notFound*`), not a broken screen.
 *
 * The one thing this screen must not let a guest lose is the way back to their
 * booking: the `manage_token` is the only credential for it, so BookDone both
 * carries it forward into the "จัดการการจอง" link and tells the guest to keep
 * this page's link (`book.saveLinkNote`).
 */
export default function BookDone() {
  const {t, language} = useI18n();
  const {params, query} = useRouter();

  const token = (params.token ?? '').trim() || null;
  const tenantId = (query.get(QR_PARAMS.tenant) ?? '').trim() || null;

  const seed = useMemo<Booking | null>(() => {
    const stored = token ? findBookingByToken(token) : null;
    if (!stored) return null;
    return {
      booking_id: stored.bookingId,
      tenant_id: stored.tenantId,
      status: 'confirmed',
      name: stored.name,
      phone: stored.phone,
      party_size: stored.partySize,
      booking_time: stored.bookingTime,
      reference: stored.reference ?? null,
    };
  }, [token]);

  const {status, booking} = useManagedBooking(token, tenantId, seed);

  const notFound = status === 'notFound' && !booking;
  const loading = status === 'loading' && !booking;

  return (
    <Screen className="bookdone">
      <style href="bookdone" precedence="medium">
        {CSS}
      </style>

      <TopBar trailing={<LanguageToggle />} />

      {notFound ? (
        <>
          <ScreenBody center>
            <Card aria-live="polite">
              <Stack>
                <h1 className="pk-h1">{t('book.notFoundTitle')}</h1>
                <p className="pk-muted">{t('book.notFoundBody')}</p>
              </Stack>
            </Card>
          </ScreenBody>
          <ScreenFooter bordered={false}>
            {tenantId ? (
              <ActionLink
                to={href(ROUTES.book, {}, {[QR_PARAMS.tenant]: tenantId})}
                variant="secondary"
              >
                {t('book.title')}
              </ActionLink>
            ) : null}
          </ScreenFooter>
        </>
      ) : loading ? (
        <ScreenBody center>
          <p className="pk-muted" aria-live="polite">
            {t('common.loading')}
          </p>
        </ScreenBody>
      ) : booking ? (
        <>
          <ScreenBody className="bookdone-body">
            <div className="bookdone-hero">
              <span className="bookdone-check" aria-hidden="true">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5l4.5 4.5L19 7" />
                </svg>
              </span>
              <h1 className="pk-h1">{t('book.doneTitle')}</h1>
              <p className="pk-muted">{t('book.doneBody')}</p>
            </div>

            <BookingDetails booking={booking} language={language} t={t} />

            <p className="pk-hint bookdone-centred">{t('book.saveLinkNote')}</p>
          </ScreenBody>

          <ScreenFooter bordered={false}>
            {token && tenantId ? (
              <ActionLink
                to={href(ROUTES.bookManage, {token}, {[QR_PARAMS.tenant]: tenantId})}
                variant="primary"
              >
                {t('book.manageAction')}
              </ActionLink>
            ) : null}
            <p className="pk-hint bookdone-centred">{t('common.attribution')}</p>
          </ScreenFooter>
        </>
      ) : (
        <ScreenBody center>
          <p className="pk-muted" aria-live="polite">
            {t('common.loading')}
          </p>
        </ScreenBody>
      )}
    </Screen>
  );
}

/** The read-only booking summary card. Shared shape with BookManage. */
export function BookingDetails({
  booking,
  language,
  t,
}: {
  booking: Booking;
  language: Language;
  t: (key: MessageKey, params?: MessageParams) => string;
}) {
  const parts = splitBookingTime(booking.booking_time);
  const date = parts ? dateLabel(parts.dateIso, language) : '';
  const time = parts?.time ?? '';

  return (
    <Card as="section">
      <Stack>
        <SplitRow>
          <span className="pk-muted">{t('book.fieldDate')}</span>
          <span className="pk-strong bookdone-figure">{date}</span>
        </SplitRow>
        <SplitRow>
          <span className="pk-muted">{t('book.fieldTime')}</span>
          <span className="pk-strong bookdone-figure">{time}</span>
        </SplitRow>
        <SplitRow>
          <span className="pk-muted">{t('book.fieldParty')}</span>
          <span className="pk-strong bookdone-figure">
            {t('book.people', {count: booking.party_size})}
          </span>
        </SplitRow>
        <SplitRow>
          <span className="pk-muted">{t('book.fieldName')}</span>
          <span className="pk-strong bookdone-figure">{booking.name}</span>
        </SplitRow>
        <SplitRow>
          <span className="pk-muted">{t('book.fieldPhone')}</span>
          <span className="pk-strong bookdone-figure">{booking.phone}</span>
        </SplitRow>
        {booking.reference ? (
          <SplitRow>
            <span className="pk-muted">{t('book.fieldReference')}</span>
            <span className="pk-strong bookdone-figure">{booking.reference}</span>
          </SplitRow>
        ) : null}
      </Stack>
    </Card>
  );
}

const CSS = `
.bookdone {
  max-width: 34rem;
  margin: 0 auto;
  width: 100%;
}

.bookdone-body {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}

.bookdone-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--pokpok-space-2);
  padding-top: var(--pokpok-space-6);
}

.bookdone-check {
  width: 72px;
  height: 72px;
  border-radius: 36px;
  background: var(--color-success);
  color: var(--color-on-dark);
  display: flex;
  align-items: center;
  justify-content: center;
}

.bookdone-figure {
  font-variant-numeric: tabular-nums;
}

.bookdone-centred {
  text-align: center;
}
`;
